import React, { useState, useCallback } from 'react';
import {
  Typography,
  Button,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Send, CloudUpload } from '@mui/icons-material';
import QRCode from 'qrcode';
import { v4 as uuidv4 } from 'uuid';
import { storage } from '../config/firebase';
import { ref, getDownloadURL, uploadBytes } from 'firebase/storage';

interface ImageToQRProps {
  onQRGenerated: (imageUrl: string) => void;
}

const ImageToQR: React.FC<ImageToQRProps> = ({ onQRGenerated }) => {
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateAndResizeImage = async (file: File): Promise<Blob> => {
    const MAX_SIZE = 1024 * 1024; // 1MB
    const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

    if (!ALLOWED_TYPES.includes(file.type)) {
      throw new Error('Tipo de archivo no permitido. Solo se aceptan imágenes (JPEG, PNG, GIF, WEBP)');
    }

    if (file.size <= MAX_SIZE) {
      return file;
    }

    // Create an image element to resize
    const img = new Image();
    const imgUrl = URL.createObjectURL(file);
    
    return new Promise((resolve, reject) => {
      img.onload = () => {
        URL.revokeObjectURL(imgUrl);
        
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        
        // Calculate new dimensions while maintaining aspect ratio
        const ratio = Math.min(1, MAX_SIZE / file.size);
        width *= ratio;
        height *= ratio;
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('No se pudo crear el contexto del canvas'));
          return;
        }
        
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Error al redimensionar la imagen'));
            }
          },
          file.type,
          0.8
        );
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(imgUrl);
        reject(new Error('Error al cargar la imagen'));
      };
      
      img.src = imgUrl;
    });
  };

  const handleImageDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setError(null);
    
    const file = e.dataTransfer.files[0];
    if (!file) return;

    try {
      const resizedImage = await validateAndResizeImage(file);
      setImageFile(new File([resizedImage], file.name, { type: file.type }));
      setImagePreview(URL.createObjectURL(resizedImage));
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Error al procesar la imagen');
    }
  }, []);

  const handleImageDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  const handleSubmit = async () => {
    if (!imageFile) return;

    try {
      setLoading(true);
      setError(null);

      // Resize image if needed
      const resizedImage = await validateAndResizeImage(imageFile);
      // Generate unique identifier and file extension
      const uniqueId = uuidv4();
      const ext = imageFile.type.split('/')[1] || 'png';
      const fileName = `images/${uniqueId}.${ext}`;
      // Upload the actual image blob to Firebase Storage
      const storageRef = ref(storage, fileName);
      await uploadBytes(storageRef, resizedImage);
      // Get the download URL
      const downloadUrl = await getDownloadURL(storageRef);
      // parse url
      const base_url = process.env.REACT_APP_BASE_URL;
      const bucket = 'images';
      const token = downloadUrl.split('=')[1];
      const full_url = `${base_url}/image.html?bucket=${bucket}&file_id=${uniqueId}&ext=${ext}&token=${token}`;
      // Generate QR code as data URL
      const qrDataUrl = await QRCode.toDataURL(full_url, {
        errorCorrectionLevel: 'H',
        margin: 1,
        width: 300,
        color: {
          dark: '#000000',
          light: '#ffffff'
        }
      });
      onQRGenerated(qrDataUrl);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Error al generar el QR');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Typography variant="h4" gutterBottom>
        Imagen a QR
      </Typography>

      <Box
        sx={{
          border: '2px dashed #ccc',
          borderRadius: 2,
          p: 3,
          textAlign: 'center',
          mb: 2,
          backgroundColor: '#fafafa',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: '#f0f0f0',
          },
        }}
        onDrop={handleImageDrop}
        onDragOver={handleImageDragOver}
      >
        <CloudUpload sx={{ fontSize: 48, color: '#666', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Arrastra y suelta una imagen aquí
        </Typography>
        <Typography variant="body2" color="textSecondary">
          o haz clic para seleccionar un archivo
        </Typography>
        <input
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              validateAndResizeImage(file)
                .then((resizedImage) => {
                  setImageFile(new File([resizedImage], file.name, { type: file.type }));
                  setImagePreview(URL.createObjectURL(resizedImage));
                })
                .catch((error) => {
                  setError(error instanceof Error ? error.message : 'Error al procesar la imagen');
                });
            }
          }}
          id="image-upload"
        />
        <label htmlFor="image-upload">
          <Button
            variant="contained"
            component="span"
            sx={{ mt: 2 }}
          >
            Seleccionar Imagen
          </Button>
        </label>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {imagePreview && (
        <Box sx={{ mb: 2, textAlign: 'center' }}>
          <img
            src={imagePreview}
            alt="Vista previa"
            style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain' }}
          />
        </Box>
      )}

      <Button
        variant="contained"
        color="primary"
        size="large"
        onClick={handleSubmit}
        disabled={loading || !imageFile}
        startIcon={loading ? <CircularProgress size={20} /> : <Send />}
        fullWidth
      >
        {loading ? 'Generando...' : 'Generar QR'}
      </Button>
    </>
  );
};

export default ImageToQR; 